import { Component, OnInit, OnDestroy } from '@angular/core';
import { SidebarMenu } from '../../../models/models';
import { AuthService } from '../../auth/auth.service';
import { Role, SIDEBAR_MENU_ITEMS } from '../constants';
import { MenuItem } from 'primeng/api';
import { EchoPermissionChangeService, PermissionChangeEvent } from '../../../services/echo-permission-change.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { WorkQueueCounts, WorkQueueCountsService } from '../../../services/work-queue-counts.service';
import { FavoritesService } from '../../../services/favorites.service';
import { MessageService } from 'primeng/api';

interface MenuBadgeItem {
	count: number;
	backgroundColor: string;
	textColor: string;
	tooltip?: string;
}

/**
 * Favorites toasts must be keyed. PrimeNG's <p-messages> subscribes to
 * MessageService and renders any message that has no key, so keyless favorites
 * messages were also appearing inline in the search-result message areas on
 * pages such as User Search and Order Search. Must match the key on the
 * <p-toast> in app-menu.component.html.
 */
const FAVORITES_TOAST_KEY = 'favorites';

// Extend MenuItem interface to include permission property
interface ExtendedMenuItem extends MenuItem {
	permission?: string;
	badgeCount?: number;
	badgeItems?: MenuBadgeItem[];
	badgeLoading?: boolean;
	onExpand?: () => void;
	/** Set false to opt an item out of the favorites star (e.g. Home). */
	favoritable?: boolean;
	items?: ExtendedMenuItem[];
}

@Component({
	selector: 'app-menu',
	templateUrl: './app-menu.component.html',
	styleUrls: ['./app-menu.component.scss']
})
export class AppMenuComponent implements OnInit, OnDestroy {
	menu: SidebarMenu[] = [];
	private permissionChangeSubscription?: Subscription;
	private workQueueCountsSubscription?: Subscription;
	private favoritesSubscription?: Subscription;
	private favoriteToggleResultSubscription?: Subscription;
	private favoritesLoadErrorSubscription?: Subscription;
	private currentUserPermissions: string[] = [];
	private currentFavoriteKeys: string[] = [];
	private workQueueCountsLoading = false;
	private workQueueCounts: WorkQueueCounts = {
		order_intake: 0,
		order_intake_sources: {
			online_order: 0,
			hl7_discharge_order: 0
		},
		inbound_communication: {
			pst_note: 0,
			online_note: 0
		},
		data_management: 0
	};

	constructor(
		private authService: AuthService,
		private echoPermissionChangeService: EchoPermissionChangeService,
		private router: Router,
		private workQueueCountsService: WorkQueueCountsService,
		private favoritesService: FavoritesService,
		private messageService: MessageService
	) {}

	ngOnInit() {
		this.loadWorkQueueCounts();

		this.favoritesSubscription = this.favoritesService.favoriteKeys$.subscribe((favoriteKeys) => {
			this.currentFavoriteKeys = favoriteKeys;
			// Re-render the menu so the My Favorites group reflects the latest list
			this.menu = this.getMenuItemsForPermissions(this.currentUserPermissions);
		});
		// Subscribed here rather than in AppMenuitemComponent: this component is a
		// single instance, so each toggle produces exactly one toast instead of one
		// per menu item in the sidebar.
		this.favoriteToggleResultSubscription = this.favoritesService.toggleResult$.subscribe((result) => {
			const name = result.label ?? 'Menu item';

			if (result.success) {
				this.messageService.add({
					key: FAVORITES_TOAST_KEY,
					severity: 'success',
					summary: result.favorited ? 'Added to My Favorites' : 'Removed from My Favorites',
					detail: `${name} was ${result.favorited ? 'added to' : 'removed from'} My Favorites.`
				});
			} else {
				this.messageService.add({
					key: FAVORITES_TOAST_KEY,
					severity: 'error',
					summary: 'Error',
					detail: `Failed to ${result.favorited ? 'add' : 'remove'} ${name}. Please try again.`
				});
			}
		});

		this.favoritesLoadErrorSubscription = this.favoritesService.loadError$.subscribe(() => {
			this.messageService.add({
				key: FAVORITES_TOAST_KEY,
				severity: 'warn',
				summary: 'My Favorites unavailable',
				detail: 'Could not load your favorites. They may not appear in the sidebar.'
			});
		});

		this.favoritesService.loadFavorites();

		this.authService.getUserRolesAndPermissions().subscribe((response) => {
			const { roles, permissions } = response;

			// Ensure permissions is always an array
			const safePermissions = Array.isArray(permissions)
				? permissions
				: typeof permissions === 'object'
					? Object.values(permissions)
					: [];

			//only update the permission when there is a change
			const oldPermissions = this.authService.getPermissions();

			if (JSON.stringify(oldPermissions.sort()) !== JSON.stringify(safePermissions.sort())) {
				this.authService.setPermissions(safePermissions);
			}

			this.currentUserPermissions = safePermissions;

			this.menu = this.getMenuItemsForPermissions(safePermissions);
			this.handleNoAccessRedirect(safePermissions);
		});

		// Listen for real-time permission changes
		this.setupPermissionChangeListener();
	}

	ngOnDestroy() {
		if (this.permissionChangeSubscription) {
			this.permissionChangeSubscription.unsubscribe();
		}
		if (this.workQueueCountsSubscription) {
			this.workQueueCountsSubscription.unsubscribe();
		}
		if (this.favoritesSubscription) {
			this.favoritesSubscription.unsubscribe();
		}
		if (this.favoriteToggleResultSubscription) {
			this.favoriteToggleResultSubscription.unsubscribe();
		}
		if (this.favoritesLoadErrorSubscription) {
			this.favoritesLoadErrorSubscription.unsubscribe();
		}
	}

	/**
	 * getMenuItemsForPermissions() returns a brand-new wrapper object on every
	 * call (e.g. every favorites change), so without trackBy Angular's default
	 * reference-based diffing tears down and recreates the entire root
	 * component - and everything nested under it - on every rebuild. There is
	 * only ever one root wrapper item, so tracking by index is sufficient and
	 * lets Angular reuse the existing component instance instead.
	 */
	trackByIndex(index: number): number {
		return index;
	}

	private getMenuItemsForPermissions(permissions: string[]): SidebarMenu[] {
		const sidebarMenu: SidebarMenu = { items: [] };

		// add the Home item as the first entry
		const homeMenuItem: ExtendedMenuItem = {
			label: 'Home',
			icon: 'pi pi-fw pi-home',
			// The app root, not a named page. app-routing redirects '' to the
			// default route, so Home follows whatever that default becomes instead
			// of pinning itself to one destination — it previously pointed at
			// '/inbound-processing', which is Order Intake's own route, so both
			// items matched the URL and the sidebar highlighted two places at once.
			//
			// Pointing it at the current default ('/patients') would just move the
			// clash to Search Patients, which already owns that route. '/' avoids it
			// entirely: the redirect fires immediately, so the URL is never '/' and
			// routerLinkActive's exact match never fires for Home. It behaves as a
			// shortcut, and the item that actually owns the destination keeps the
			// highlight.
			routerLink: ['/'],
			// Home is always present at the top of the sidebar, so favoriting it
			// would only duplicate it a few pixels below.
			favoritable: false
		};

		sidebarMenu.items.push(homeMenuItem);

		const filteredMenuItems = this.applyFavorites(
			this.applyWorkQueueCounts(this.filterMenuItemsByPermissions(SIDEBAR_MENU_ITEMS, permissions))
		);
		sidebarMenu.items.push(...filteredMenuItems);

		return [sidebarMenu];
	}

	private filterMenuItemsByPermissions(menuItems: ExtendedMenuItem[], permissions: string[]): ExtendedMenuItem[] {
		const safePermissions = Array.isArray(permissions) ? permissions : [];

		return menuItems.reduce<ExtendedMenuItem[]>((acc, item) => {
			if (item.permission && !safePermissions.includes(item.permission)) {
				return acc;
			}

			if (item.items && item.items.length > 0) {
				const filteredSubItems = this.filterMenuItemsByPermissions(item.items, safePermissions); // recursion for nested items
				// exclude permission-gated parents whose every child was filtered out
				if (filteredSubItems.length === 0 && item.permission) {
					return acc;
				}
				acc.push({ ...item, items: filteredSubItems });
			} else {
				acc.push(item);
			}

			return acc;
		}, []);
	}

	private loadWorkQueueCounts(): void {
		this.workQueueCountsLoading = true;
		this.updateExistingWorkQueueBadges();

		this.workQueueCountsSubscription?.unsubscribe();
		this.workQueueCountsSubscription = this.workQueueCountsService.getCounts().subscribe({
			next: (counts) => {
				this.workQueueCounts = counts;
				this.workQueueCountsLoading = false;
				this.updateExistingWorkQueueBadges();
			},
			error: () => {
				this.workQueueCountsLoading = false;
				this.updateExistingWorkQueueBadges();
			}
		});
	}

	private applyWorkQueueCounts(menuItems: ExtendedMenuItem[]): ExtendedMenuItem[] {
		return menuItems.map((item) => {
			if (item.label !== 'Inbound Processing' || !item.items) {
				return item;
			}

			return {
				...item,
				onExpand: () => this.loadWorkQueueCounts(),
				items: item.items.map((child) => ({
					...child,
					badgeCount: this.getWorkQueueBadgeCount(child),
					badgeItems: this.getWorkQueueBadgeItems(child),
					badgeLoading: this.workQueueCountsLoading
				}))
			};
		});
	}

	/**
	 * Populates the "My Favorites" group's items from the already
	 * permission-filtered menu tree. Only leaf items (routerLink, no nested
	 * items) whose key (routerLink.join('/')) is in the current favorites list
	 * are included. Items are shallow-copied into the favorites group, not
	 * moved - they remain in their original location in the tree as well. If a
	 * favorited item's routerLink is no longer present in the filtered tree
	 * (e.g. the user lost permission to it), it is simply omitted here - the
	 * underlying favorite record itself is left untouched.
	 */
	private applyFavorites(menuItems: ExtendedMenuItem[]): ExtendedMenuItem[] {
		// Until favorites load, hide the group entirely rather than rendering it
		// empty - an empty "My Favorites" is indistinguishable from "you have
		// none", and on a slow connection it would show, then pop in.
		if (!this.favoritesService.isLoaded) {
			return menuItems.map((item) => (item.label === 'My Favorites' ? { ...item, visible: false } : item));
		}

		const favoriteItems = this.collectFavoriteLeafItems(menuItems);

		return menuItems.map((item) => {
			if (item.label !== 'My Favorites') {
				return item;
			}

			return {
				...item,
				visible: true,
				items: favoriteItems
			};
		});
	}

	private collectFavoriteLeafItems(menuItems: ExtendedMenuItem[]): ExtendedMenuItem[] {
		const favorites: ExtendedMenuItem[] = [];

		for (const item of menuItems) {
			if (item.items && item.items.length > 0) {
				favorites.push(...this.collectFavoriteLeafItems(item.items));
				continue;
			}

			const key = item.routerLink ? (item.routerLink as string[]).join('/') : undefined;
			if (key && this.currentFavoriteKeys.includes(key)) {
				// favoriteCopy marks this as a duplicate of an item that also lives
				// elsewhere in the tree. The copy must not broadcast route-active
				// state (see updateActiveStateFromRoute in app-menuitem.component),
				// otherwise it fights with the canonical item and collapses whichever
				// group that one lives under.
				favorites.push({ ...item, favoriteCopy: true });
			}
		}

		return favorites;
	}

	private getWorkQueueBadgeCount(item: ExtendedMenuItem): number {
		const route = item.routerLink?.[0];

		switch (route) {
			case 'inbound-processing':
				return this.workQueueCounts.order_intake;
			case 'inbound-communication':
				return (
					this.workQueueCounts.inbound_communication.pst_note +
					this.workQueueCounts.inbound_communication.online_note
				);
			case 'data-management':
				return this.workQueueCounts.data_management;
			default:
				return item.badgeCount ?? 0;
		}
	}

	private getWorkQueueBadgeItems(item: ExtendedMenuItem): MenuBadgeItem[] {
		const route = item.routerLink?.[0];

		switch (route) {
			case 'inbound-processing':
				return [
					this.createBadgeItem(
						this.workQueueCounts.order_intake_sources?.online_order ?? 0,
						'var(--badge-work-queue-bg)',
						'var(--badge-work-queue-text)',
						'Online Orders'
					),
					this.createBadgeItem(
						this.workQueueCounts.order_intake_sources?.hl7_discharge_order ?? 0,
						'var(--badge-hl7-discharge-bg)',
						'var(--badge-hl7-discharge-text)',
						'HL7 Discharge Orders'
					)
				];
			case 'inbound-communication':
				return [
					this.createBadgeItem(
						this.workQueueCounts.inbound_communication.pst_note,
						'var(--badge-work-queue-bg)',
						'var(--badge-work-queue-text)',
						'PST Notes'
					),
					this.createBadgeItem(
						this.workQueueCounts.inbound_communication.online_note,
						'var(--badge-online-note-bg)',
						'var(--badge-online-note-text)',
						'Online Notes'
					)
				];
			case 'data-management':
				return [
					this.createBadgeItem(
						this.workQueueCounts.data_management,
						'var(--badge-work-queue-bg)',
						'var(--badge-work-queue-text)'
					)
				];
			default:
				return item.badgeCount !== undefined
					? [
							this.createBadgeItem(
								item.badgeCount,
								'var(--badge-work-queue-bg)',
								'var(--badge-work-queue-text)'
							)
						]
					: [];
		}
	}

	private createBadgeItem(
		count: number,
		backgroundColor: string,
		textColor: string,
		tooltip?: string
	): MenuBadgeItem {
		return {
			count,
			backgroundColor,
			textColor,
			tooltip
		};
	}

	private updateExistingWorkQueueBadges(): void {
		const rootMenu = this.menu[0];
		if (!rootMenu?.items?.length) {
			return;
		}

		const inboundProcessingItem = rootMenu.items.find((item: any) => item.label === 'Inbound Processing') as
			| ExtendedMenuItem
			| undefined;

		if (!inboundProcessingItem?.items?.length) {
			return;
		}

		inboundProcessingItem.items = inboundProcessingItem.items.map((child) => ({
			...child,
			badgeCount: this.getWorkQueueBadgeCount(child),
			badgeItems: this.getWorkQueueBadgeItems(child),
			badgeLoading: this.workQueueCountsLoading
		}));
	}

	private handleNoAccessRedirect(permissions: string[]): void {
		const hasMenuPermissions = SIDEBAR_MENU_ITEMS.some(
			(item) => !item['permission'] || permissions.includes(item['permission'])
		);

		const onNoAccessPage = this.router.url.startsWith('/no-access');

		if (!hasMenuPermissions && !onNoAccessPage) {
			this.router.navigate(['/no-access']);
		} else if (hasMenuPermissions && onNoAccessPage) {
			this.router.navigate(['/']);
		}
	}

	private setupPermissionChangeListener(): void {
		this.permissionChangeSubscription = this.echoPermissionChangeService.permissionChange$.subscribe(
			(event: PermissionChangeEvent) => {
				// Update current user permissions based on changes
				const updatedPermissions = [...this.currentUserPermissions];

				// Remove permissions that were removed
				event.removedPermissions.forEach((permission) => {
					const index = updatedPermissions.indexOf(permission);
					if (index > -1) {
						updatedPermissions.splice(index, 1);
					}
				});

				// Add permissions that were added
				event.addedPermissions.forEach((permission) => {
					if (!updatedPermissions.includes(permission)) {
						updatedPermissions.push(permission);
					}
				});

				this.currentUserPermissions = updatedPermissions;

				// Re-render the menu with updated permissions
				this.menu = this.getMenuItemsForPermissions(this.currentUserPermissions);
				this.handleNoAccessRedirect(this.currentUserPermissions);
			}
		);
	}
}
